/**
 * Per-block property inspector. Renders the right form for whichever
 * block type is selected. Each inspector calls onChange with a fully
 * replaced block (immer handles the immutable update in the parent).
 */
import type { KitePageBlock, KitePageTheme, KiteFormField } from '@xsc/shared';

type Patcher<T> = (next: T) => void;

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs font-medium text-ink-muted">
      <span className="block mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded border border-border bg-bg px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-accent';

export function ThemeInspector({
  theme,
  onChange,
}: {
  theme: KitePageTheme;
  onChange: Patcher<KitePageTheme>;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Page theme</h3>
      <Field label="Background color">
        <input className={inputCls} type="text"
          value={theme.background_color}
          onChange={(e) => onChange({ ...theme, background_color: e.target.value })} />
      </Field>
      <Field label="Text color">
        <input className={inputCls} type="text"
          value={theme.text_color}
          onChange={(e) => onChange({ ...theme, text_color: e.target.value })} />
      </Field>
      <Field label="Accent color (buttons, links)">
        <input className={inputCls} type="text"
          value={theme.accent_color}
          onChange={(e) => onChange({ ...theme, accent_color: e.target.value })} />
      </Field>
      <Field label="Font family">
        <select className={inputCls}
          value={theme.font_family}
          onChange={(e) => onChange({ ...theme, font_family: e.target.value as KitePageTheme['font_family'] })}>
          <option value="system">System</option>
          <option value="serif">Serif</option>
          <option value="mono">Monospace</option>
          <option value="inter">Inter</option>
          <option value="roboto">Roboto</option>
        </select>
      </Field>
      <Field label={`Max width: ${theme.max_width_px}px`}>
        <input className="w-full" type="range" min={320} max={1280} step={20}
          value={theme.max_width_px}
          onChange={(e) => onChange({ ...theme, max_width_px: Number(e.target.value) })} />
      </Field>
    </div>
  );
}

/**
 * Type-safe per-block prop patcher. The generic parameter `T` is the
 * concrete block variant (narrowed from the discriminated union), and
 * the returned function only accepts props matching that variant's
 * `props` shape. This replaces the previous `as unknown as KitePageBlock`
 * escape hatch with a discriminator-preserving narrowing.
 */
function makePropsPatcher<T extends KitePageBlock>(
  block: T,
  onChange: Patcher<KitePageBlock>,
): (props: T['props']) => void {
  return (props) => {
    // Reconstruct the block with the same `type` discriminator and the
    // new props. The conditional is purely for TS — at runtime the
    // spread does the same thing for every variant — but it lets the
    // compiler verify each branch matches its own variant.
    onChange({ ...block, props } as T);
  };
}

export function BlockInspector({
  block,
  onChange,
}: {
  block: KitePageBlock;
  onChange: Patcher<KitePageBlock>;
}): JSX.Element {
  // Each branch narrows `block` to a specific variant of the
  // discriminated union, then constructs a `patch` function that only
  // accepts props matching that variant. No `as unknown` casts.
  function renderBody(): JSX.Element | null {
    if (block.type === 'hero') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          <Field label="Headline">
            <input className={inputCls} value={block.props.headline}
              onChange={(e) => patch({ ...block.props, headline: e.target.value })} />
          </Field>
          <Field label="Subheadline">
            <input className={inputCls} value={block.props.subheadline}
              onChange={(e) => patch({ ...block.props, subheadline: e.target.value })} />
          </Field>
          <Field label="Background image URL (optional)">
            <input className={inputCls} value={block.props.background_image_url ?? ''}
              onChange={(e) => patch({ ...block.props, background_image_url: e.target.value })} />
          </Field>
        </div>
      );
    }
    if (block.type === 'text') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <Field label="Body (supports {{variables}})">
          <textarea className={inputCls} rows={6}
            value={block.props.body}
            onChange={(e) => patch({ ...block.props, body: e.target.value })} />
        </Field>
      );
    }
    if (block.type === 'image') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          <Field label="Image URL">
            <input className={inputCls} value={block.props.url}
              onChange={(e) => patch({ ...block.props, url: e.target.value })} />
          </Field>
          <Field label="Alt text">
            <input className={inputCls} value={block.props.alt}
              onChange={(e) => patch({ ...block.props, alt: e.target.value })} />
          </Field>
          <Field label={`Width: ${block.props.width_pct}%`}>
            <input type="range" min={10} max={100} step={5} className="w-full"
              value={block.props.width_pct}
              onChange={(e) => patch({ ...block.props, width_pct: Number(e.target.value) })} />
          </Field>
        </div>
      );
    }
    if (block.type === 'button') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          <Field label="Label">
            <input className={inputCls} value={block.props.label}
              onChange={(e) => patch({ ...block.props, label: e.target.value })} />
          </Field>
          <Field label="Link URL (supports {{variables}})">
            <input className={inputCls} value={block.props.href}
              onChange={(e) => patch({ ...block.props, href: e.target.value })} />
          </Field>
          <Field label="Variant">
            <select className={inputCls} value={block.props.variant}
              onChange={(e) => patch({ ...block.props, variant: e.target.value as 'primary' | 'secondary' })}>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary (outlined)</option>
            </select>
          </Field>
        </div>
      );
    }
    if (block.type === 'form') {
      return <FormFieldsInspector block={block} patchProps={makePropsPatcher(block, onChange)} />;
    }
    if (block.type === 'video') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          <Field label="Video URL (YouTube / Vimeo / MP4)">
            <input className={inputCls} value={block.props.url}
              onChange={(e) => patch({ ...block.props, url: e.target.value })} />
          </Field>
          <Field label="Caption (optional)">
            <input className={inputCls} value={block.props.caption}
              onChange={(e) => patch({ ...block.props, caption: e.target.value })} />
          </Field>
        </div>
      );
    }
    if (block.type === 'faq') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          {block.props.items.map((item, idx) => (
            <div key={idx} className="border border-border rounded p-2 space-y-2">
              <Field label={`Q${idx + 1}`}>
                <input className={inputCls} value={item.question}
                  onChange={(e) => {
                    const items = block.props.items.slice();
                    items[idx] = { ...item, question: e.target.value };
                    patch({ ...block.props, items });
                  }} />
              </Field>
              <Field label="Answer">
                <textarea className={inputCls} rows={3} value={item.answer}
                  onChange={(e) => {
                    const items = block.props.items.slice();
                    items[idx] = { ...item, answer: e.target.value };
                    patch({ ...block.props, items });
                  }} />
              </Field>
              <button type="button" className="text-xs text-red-500"
                onClick={() => patch({
                  ...block.props,
                  items: block.props.items.filter((_, i) => i !== idx),
                })}
              >Remove</button>
            </div>
          ))}
          <button type="button" className="text-sm text-accent underline"
            onClick={() => patch({
              ...block.props,
              items: [...block.props.items, { question: 'New question', answer: 'Answer here' }],
            })}
          >+ Add question</button>
        </div>
      );
    }
    if (block.type === 'spacer') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <Field label={`Height: ${block.props.height_px}px`}>
          <input type="range" min={4} max={256} step={4} className="w-full"
            value={block.props.height_px}
            onChange={(e) => patch({ ...block.props, height_px: Number(e.target.value) })} />
        </Field>
      );
    }
    if (block.type === 'footer') {
      const patch = makePropsPatcher(block, onChange);
      return (
        <div className="space-y-3">
          <Field label="Footer text">
            <textarea className={inputCls} rows={3} value={block.props.text}
              onChange={(e) => patch({ ...block.props, text: e.target.value })} />
          </Field>
          <Field label="Unsubscribe URL (optional, supports {{variables}})">
            <input className={inputCls} value={block.props.unsubscribe_url}
              onChange={(e) => patch({ ...block.props, unsubscribe_url: e.target.value })} />
          </Field>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold capitalize">{block.type} block</h3>
      {renderBody()}
      <StyleInspector block={block} onChange={onChange} />
    </div>
  );
}

function FormFieldsInspector({
  block,
  patchProps,
}: {
  block: Extract<KitePageBlock, { type: 'form' }>;
  patchProps: (p: Extract<KitePageBlock, { type: 'form' }>['props']) => void;
}) {
  const updateField = (idx: number, patch: Partial<KiteFormField>) => {
    const fields = block.props.fields.slice();
    fields[idx] = { ...fields[idx]!, ...patch } as KiteFormField;
    patchProps({ ...block.props, fields });
  };
  return (
    <div className="space-y-3">
      <Field label="Submit button label">
        <input className={inputCls} value={block.props.submit_label}
          onChange={(e) => patchProps({ ...block.props, submit_label: e.target.value })} />
      </Field>
      <Field label="Success message">
        <input className={inputCls} value={block.props.success_message}
          onChange={(e) => patchProps({ ...block.props, success_message: e.target.value })} />
      </Field>
      <Field label="Redirect URL on success (optional)">
        <input className={inputCls} value={block.props.redirect_url ?? ''}
          onChange={(e) => patchProps({ ...block.props, redirect_url: e.target.value })} />
      </Field>
      <Field label="Notify email on submission (optional)">
        <input className={inputCls} type="email" placeholder="ops@example.com"
          value={block.props.notify_email ?? ''}
          onChange={(e) => patchProps({ ...block.props, notify_email: e.target.value })} />
      </Field>
      <div className="border-t border-border pt-3">
        <div className="text-xs font-semibold mb-2">Fields</div>
        {block.props.fields.map((f, idx) => (
          <div key={idx} className="border border-border rounded p-2 space-y-2 mb-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Name (snake_case)">
                <input className={inputCls} value={f.name}
                  onChange={(e) => updateField(idx, { name: e.target.value })} />
              </Field>
              <Field label="Type">
                <select className={inputCls} value={f.type}
                  onChange={(e) => updateField(idx, { type: e.target.value as KiteFormField['type'] })}>
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="tel">Phone</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Select</option>
                  <option value="checkbox">Checkbox</option>
                </select>
              </Field>
            </div>
            <Field label="Label">
              <input className={inputCls} value={f.label}
                onChange={(e) => updateField(idx, { label: e.target.value })} />
            </Field>
            {f.type === 'select' && (
              <Field label="Options (one per line)">
                <textarea className={inputCls} rows={3}
                  value={(f.options ?? []).join('\n')}
                  onChange={(e) => updateField(idx, {
                    options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                  })} />
              </Field>
            )}
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={f.required}
                onChange={(e) => updateField(idx, { required: e.target.checked })} />
              Required
            </label>
            <button type="button" className="text-xs text-red-500"
              onClick={() => patchProps({
                ...block.props,
                fields: block.props.fields.filter((_, i) => i !== idx),
              })}
            >Remove field</button>
          </div>
        ))}
        <button type="button"
          className="text-sm text-accent underline"
          onClick={() => patchProps({
            ...block.props,
            fields: [
              ...block.props.fields,
              { name: `field_${block.props.fields.length + 1}`, label: 'New field', type: 'text', required: false },
            ],
          })}
        >+ Add field</button>
      </div>
    </div>
  );
}

function StyleInspector({
  block,
  onChange,
}: {
  block: KitePageBlock;
  onChange: Patcher<KitePageBlock>;
}) {
  const style = block.style ?? {};
  // Discriminator-preserving style patcher: we keep `block.type` and
  // `block.props` intact, only merging into `style`, so the resulting
  // object is structurally still the same variant of the union.
  function patchStyle(patch: Partial<typeof style>): void {
    type Variant = typeof block;
    const next: Variant = { ...block, style: { ...style, ...patch } } as Variant;
    onChange(next);
  }
  return (
    <details className="border-t border-border pt-3">
      <summary className="text-xs font-semibold cursor-pointer">Block style</summary>
      <div className="space-y-3 mt-2">
        <Field label="Background color">
          <input className={inputCls} value={style.background_color ?? ''}
            onChange={(e) => patchStyle({ background_color: e.target.value || undefined })} />
        </Field>
        <Field label="Text color">
          <input className={inputCls} value={style.text_color ?? ''}
            onChange={(e) => patchStyle({ text_color: e.target.value || undefined })} />
        </Field>
        <Field label="Font size (px)">
          <input className={inputCls} type="number" min={8} max={96}
            value={style.font_size_px ?? ''}
            onChange={(e) => patchStyle({ font_size_px: e.target.value ? Number(e.target.value) : undefined })} />
        </Field>
        <Field label="Padding Y (px)">
          <input className={inputCls} type="number" min={0} max={256}
            value={style.padding_y_px ?? ''}
            onChange={(e) => patchStyle({ padding_y_px: e.target.value ? Number(e.target.value) : undefined })} />
        </Field>
        <Field label="Padding X (px)">
          <input className={inputCls} type="number" min={0} max={256}
            value={style.padding_x_px ?? ''}
            onChange={(e) => patchStyle({ padding_x_px: e.target.value ? Number(e.target.value) : undefined })} />
        </Field>
        <Field label="Text align">
          <select className={inputCls} value={style.align ?? ''}
            onChange={(e) => patchStyle({ align: (e.target.value || undefined) as 'left' | 'center' | 'right' | undefined })}>
            <option value="">Default</option>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </Field>
      </div>
    </details>
  );
}
